
import React from 'react';
import { Bookmark, ArchiveFile } from '../types';
import WikiHeader from './WikiHeader';

interface ProfileCategoryProps {
  categoryPath: string;
  bookmarks: Bookmark[];
  onNavigateToBookmark: (bookmark: Bookmark) => void;
  onDeleteBookmark: (id: string) => void;
  onBack: () => void;
  onMenuClick: () => void;
  isSidebarOpen: boolean;
}

const ProfileCategory: React.FC<ProfileCategoryProps> = ({
  categoryPath,
  bookmarks,
  onNavigateToBookmark,
  onDeleteBookmark,
  onBack,
  onMenuClick,
  isSidebarOpen
}) => {
  const filtered = bookmarks.filter(b => b.categoryPath === categoryPath);
  const displayName = categoryPath.split('/').pop() || categoryPath;

  return (
    <div className="flex flex-col h-full bg-[#fbfaf6] dark:bg-stone-900 transition-colors duration-300">
      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 lg:p-10">
        <div className="max-w-4xl mx-auto space-y-4 pb-20">
          {filtered.map(bookmark => (
            <div 
              key={bookmark.id}
              className="group bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-2xl p-6 hover:border-amber-500/50 transition-all cursor-pointer shadow-sm relative"
              onClick={() => onNavigateToBookmark(bookmark)}
            >
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-[0.2em]">{bookmark.categoryPath}</span>
                    <span className="text-[10px] text-stone-300">•</span>
                    <span className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">{new Date(bookmark.timestamp).toLocaleDateString()}</span>
                  </div>
                  <h4 className="text-lg font-serif font-bold text-stone-800 dark:text-stone-100 group-hover:text-amber-600 transition-colors">
                    {bookmark.title || bookmark.fileName || 'Untitled'}
                  </h4>
                  <p className="text-xs text-stone-500 mt-2 flex items-center gap-2">
                    <i className="fa-solid fa-scroll text-[10px] opacity-40"></i>
                    {bookmark.fileName}
                  </p>
                  {bookmark.paragraphText && (
                    <p className="text-sm text-stone-500 dark:text-stone-400 mt-4 italic line-clamp-3 bg-stone-50 dark:bg-stone-900/50 p-4 rounded-xl border-l-4 border-amber-500/20">
                      "{bookmark.paragraphText}"
                    </p>
                  )}
                  {bookmark.note && (
                    <div className="mt-4 p-4 bg-amber-50/50 dark:bg-amber-900/10 rounded-xl border border-amber-100/50 dark:border-amber-900/30">
                      <p className="text-[10px] font-black text-amber-600 uppercase mb-2 tracking-widest">Personal Note</p>
                      <p className="text-sm text-stone-700 dark:text-stone-300 leading-relaxed">{bookmark.note}</p>
                    </div>
                  )}
                </div>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteBookmark(bookmark.id);
                  }}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-stone-50 hover:bg-red-50 text-stone-300 hover:text-red-500 transition-all"
                  title="Remove from collection"
                >
                  <i className="fa-solid fa-trash-can text-sm"></i>
                </button>
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-20 bg-stone-50 dark:bg-stone-900/30 rounded-2xl border-2 border-dashed border-stone-200 dark:border-stone-800">
               <i className="fa-solid fa-bookmark text-stone-200 dark:text-stone-700 text-6xl mb-4"></i>
               <h4 className="font-bold text-stone-800 dark:text-stone-200 uppercase tracking-widest">Folder Empty</h4>
               <p className="text-sm text-stone-400 mt-2">No bookmarks found in this section.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCategory;
